/**
 * Campaign search
 *
 * Searches campaigns as the admin types and lists each match with its organisation, URL, status
 * and ID. Choosing a campaign adds it and empties the field. Results belong to this field alone.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Message } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import Autocomplete, {
  AutocompleteOption
} from '../../../../components/form/autocomplete';
import { getCampaignOption } from '../../../../helpers/entityOptionHelper';
import { searchCampaigns } from '../api';
import { campaignSearchDelayMs, i18nKeys } from '../constants';
import { CampaignSearchResult } from '../types';
import Styles from './CampaignPicker.module.css';

const statusClass: Record<string, string | undefined> = {
  archived: Styles.statusArchived,
  live: Styles.statusLive
};

/** Campaign row with its status above the ID. */
const getPoolCampaignOption = (
  campaign: CampaignSearchResult
): AutocompleteOption => {
  const option = getCampaignOption(campaign);
  const status = campaign.status;
  if (!status) {
    return option;
  }
  return {
    ...option,
    aside: [
      <span
        key="status"
        className={`${Styles.status} ${statusClass[status] ?? ''}`}
      >
        {I18n.t(`${i18nKeys.PICKER.STATUS}.${status}`, {
          defaultValue: status
        })}
      </span>,
      ...(option.aside ?? [])
    ]
  };
};

interface CampaignSearchProps {
  disabled?: boolean;
  id: string;
  label: string;
  onPick: (campaignId: number) => void;
  organizationId?: number | null;
}

const CampaignSearch: React.FC<CampaignSearchProps> = ({
  disabled,
  id,
  label,
  onPick,
  organizationId
}) => {
  const [results, setResults] = useState<CampaignSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const latestRequest = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      timer.current && clearTimeout(timer.current);
    },
    []
  );

  // Only the latest search's results are kept.
  const search = (_event: any, { searchQuery }: { searchQuery: string }) => {
    const query = searchQuery.trim();
    const request = ++latestRequest.current;
    timer.current && clearTimeout(timer.current);
    if (!query) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const found = await searchCampaigns(query, organizationId);
        if (request === latestRequest.current) {
          setResults(found);
          setHasFailed(false);
        }
      } catch {
        if (request === latestRequest.current) {
          setResults([]);
          setHasFailed(true);
        }
      } finally {
        if (request === latestRequest.current) {
          setIsLoading(false);
        }
      }
    }, campaignSearchDelayMs);
  };

  return (
    <>
      <Autocomplete
        id={id}
        label={label}
        disabled={disabled}
        placeholder={I18n.t(i18nKeys.PICKER.PLACEHOLDER)}
        loading={isLoading}
        options={results.map(getPoolCampaignOption)}
        search={search}
        value=""
        onChange={(_event, data) => {
          if (data.value) {
            onPick(Number(data.value));
          }
        }}
      />
      {hasFailed && (
        <Message
          negative
          role="alert"
          content={I18n.t(i18nKeys.PICKER.SEARCH_FAILED)}
        />
      )}
    </>
  );
};

export default CampaignSearch;
